import { useCallback, useMemo, useState } from "react";

import BoardShareSheetView from "./BoardShareSheetView";
import { useBoardOwnershipTransfer } from "@/features/player-kanban/hooks/useBoardOwnershipTransfer";
import { useBoardPermissions } from "@/features/player-kanban/hooks/useBoardPermissions";
import { useBoardSharing } from "@/features/player-kanban/hooks/useBoardSharing";

import { useCurrentUser } from "@/features/auth";
import type {
  KanbanOrganizationUserFragment,
  UserKanbanBoardFragmentFragment,
} from "@/generated/graphql";
import {
  useGetKanbanBoardWithPermissionsQuery,
  useListKanbanOrganizationUsersQuery,
  UserKanbanBoardRole,
} from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";
import { formatError } from "@/utils/formatError";

/**
 * Page size for the org-users picker. The "Add User" search filters this list
 * client-side, so a small page silently hides active members beyond it (e.g.
 * Bruno Freire returned "No users found" at the old cap of 25 — SE-5378).
 * Matches the `first` used by the shared useShareModal so both call sites of
 * ListKanbanOrganizationUsers collapse into one normalized cache entry.
 */
const ORG_USERS_PAGE_SIZE = 500;

/** Represents a Cognito user attribute with Name and Value properties */
type CognitoAttribute = { Name: string; Value: string };

/**
 * Gets Cognito attributes from a user, preferring Attributes over UserAttributes
 * @param cognitoUser - The Cognito user object
 * @returns Array of Cognito attributes, preferring Attributes, then UserAttributes, then empty array
 */
const getCognitoAttributes = (
  cognitoUser?: {
    Attributes?: CognitoAttribute[] | null;
    UserAttributes?: CognitoAttribute[] | null;
  } | null
): CognitoAttribute[] =>
  cognitoUser?.Attributes ?? cognitoUser?.UserAttributes ?? [];

/**
 * Finds an attribute value by name from a list of Cognito attributes
 * @param attributes - The array of Cognito attributes
 * @param name - The attribute name to find
 * @returns The attribute value if found, empty string otherwise
 */
const getAttributeValue = (
  attributes: CognitoAttribute[],
  name: string
): string => attributes.find(attr => attr.Name === name)?.Value ?? "";

/**
 * Extracts email from user's Cognito attributes
 * @param user - The user kanban board fragment containing user data
 * @returns The user's email address, or empty string if not found
 */
const getUserEmail = (user: UserKanbanBoardFragmentFragment): string => {
  const attributes = getCognitoAttributes(user.user?.cognitoUser);
  return getAttributeValue(attributes, "email");
};

/**
 * Extracts display name from user's Cognito attributes
 * @param user - The user kanban board fragment containing user data
 * @returns The user's full name, or email prefix, or "User" as fallback
 */
const getUserName = (user: UserKanbanBoardFragmentFragment): string => {
  const attributes = getCognitoAttributes(user.user?.cognitoUser);
  const givenName = getAttributeValue(attributes, "given_name");
  const familyName = getAttributeValue(attributes, "family_name");

  const fullName = `${givenName} ${familyName}`.trim();
  return fullName || getUserEmail(user).split("@")[0] || "User";
};

/**
 * Extracts display name from organization user's Cognito attributes
 * @param orgUser - The organization user fragment containing user data
 * @returns The user's full name, email, or truncated user ID as fallback
 */
const getOrgUserDisplayName = (
  orgUser: KanbanOrganizationUserFragment
): string => {
  const attributes = getCognitoAttributes(orgUser.user?.cognitoUser);
  const givenName = getAttributeValue(attributes, "given_name");
  const familyName = getAttributeValue(attributes, "family_name");
  const email = getAttributeValue(attributes, "email");

  const fullName = `${givenName} ${familyName}`.trim();
  if (fullName) return fullName;
  if (email) return email;
  return `User ${orgUser.user?.id?.slice(0, 8) || "Unknown"}`;
};

/**
 * Role display text lookup
 */
const ROLE_DISPLAY_TEXT: Record<UserKanbanBoardRole, string> = {
  [UserKanbanBoardRole.Owner]: "Owner",
  [UserKanbanBoardRole.Editor]: "Editor",
  [UserKanbanBoardRole.Viewer]: "Viewer",
};

/**
 * Props for BoardShareSheet component
 */
export interface BoardShareSheetProps {
  boardId: string;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Container component for BoardShareSheet - manages business logic for the board sharing bottom sheet including user selection, role assignment, and access management.
 * @param props - Component props
 * @param props.boardId - Unique identifier of the kanban board to manage sharing permissions for
 * @param props.isOpen - Boolean flag controlling the visibility of the share sheet modal
 * @param props.onClose - Callback invoked when the share sheet is dismissed or closed
 */
const BoardShareSheetStateful = ({
  boardId,
  isOpen,
  onClose,
}: BoardShareSheetProps) => {
  const currentUser = useCurrentUser();
  const toast = useToastNotification();
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<UserKanbanBoardRole>(
    UserKanbanBoardRole.Viewer
  );
  const [isUserPopoverOpen, setIsUserPopoverOpen] = useState(false);
  const [isRolePopoverOpen, setIsRolePopoverOpen] = useState(false);
  const [userSearchText, setUserSearchText] = useState("");

  const { data: boardData, loading: boardLoading } =
    useGetKanbanBoardWithPermissionsQuery({
      // SE-8296: 100, matching useBoardPermissions, for two reasons. The
      // members connection is ordered `role DESC, createdAt ASC`, and on a
      // Postgres enum declared OWNER, EDITOR, VIEWER that sorts the OWNER
      // LAST — so at 25 the owner row falls off the page first on exactly the
      // boards most likely to need a transfer, and the Board owner section
      // would read "No owner assigned" for a board that has one. Matching the
      // page size also collapses both call sites into one normalized cache
      // entry instead of issuing two requests for the same connection.
      variables: { id: boardId, first: 100 },
      skip: !boardId,
    });

  const { data: usersData, loading: usersLoading } =
    useListKanbanOrganizationUsersQuery({
      variables: { first: ORG_USERS_PAGE_SIZE },
    });

  const { addUser, removeUser, updateUserRole, isAddingUser, isRemovingUser } =
    useBoardSharing(boardId);

  const currentUsers = useMemo(
    () => boardData?.getKanbanBoard?.users?.edges?.map(edge => edge.node) ?? [],
    [boardData]
  );

  const availableUsers = useMemo(() => {
    const currentUserIds = new Set(currentUsers.map(u => u.userId));

    return (
      usersData?.listMyOrganizationsOrganizationUsers?.edges
        ?.map(edge => edge.node)
        .filter(
          orgUser => orgUser.user && !currentUserIds.has(orgUser.user.id)
        ) ?? []
    );
  }, [usersData, currentUsers]);

  const filteredAvailableUsers = useMemo(() => {
    if (!userSearchText.trim()) return availableUsers;

    const searchLower = userSearchText.toLowerCase().trim();
    return availableUsers.filter(orgUser => {
      const attributes = getCognitoAttributes(orgUser.user?.cognitoUser);
      const givenName = getAttributeValue(
        attributes,
        "given_name"
      ).toLowerCase();
      const familyName = getAttributeValue(
        attributes,
        "family_name"
      ).toLowerCase();
      const email = getAttributeValue(attributes, "email").toLowerCase();

      return (
        givenName.includes(searchLower) ||
        familyName.includes(searchLower) ||
        email.includes(searchLower) ||
        `${givenName} ${familyName}`.includes(searchLower)
      );
    });
  }, [availableUsers, userSearchText]);

  const selectedUserDisplayName = useMemo(() => {
    if (!selectedUserId) return "Select a user to add";
    const user = availableUsers.find(u => u.user?.id === selectedUserId);
    return user ? getOrgUserDisplayName(user) : "Select a user to add";
  }, [selectedUserId, availableUsers]);

  const selectedRoleDisplayText = useMemo(
    () => (selectedRole ? ROLE_DISPLAY_TEXT[selectedRole] : "Select role"),
    [selectedRole]
  );

  const currentUsersWithDisplayData = useMemo(
    () =>
      currentUsers.map(user => ({
        id: user.id,
        userId: user.userId,
        role: user.role,
        userEmail: getUserEmail(user),
        userName: getUserName(user),
      })),
    [currentUsers]
  );

  const availableUsersWithDisplayData = useMemo(
    () =>
      filteredAvailableUsers.map(orgUser => ({
        userId: orgUser.user?.id ?? "",
        displayName: getOrgUserDisplayName(orgUser),
      })),
    [filteredAvailableUsers]
  );

  const handleAddUser = useCallback(async () => {
    if (!selectedUserId || !selectedRole) {
      toast.show({
        description: "Please select a user and role",
        action: "danger",
      });
      return;
    }

    // Find the selected user to get their display name
    const selectedUser = availableUsers.find(
      u => u.user?.id === selectedUserId
    );
    const attributes = getCognitoAttributes(selectedUser?.user?.cognitoUser);
    const firstName = getAttributeValue(attributes, "given_name");
    const lastName = getAttributeValue(attributes, "family_name");
    const email = getAttributeValue(attributes, "email");

    const userName =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : email || selectedUser?.user?.cognitoUser?.Username || "User";

    try {
      await addUser(selectedUserId, selectedRole);
      setSelectedUserId("");
      setSelectedRole(UserKanbanBoardRole.Viewer);
      toast.show({
        description: `Successfully added ${userName}`,
        action: "success",
      });
    } catch (error) {
      const errorMessage =
        formatError(error)?.message ||
        `An issue occurred. Please make sure ${userName} is still active in the org`;

      toast.show({
        description: errorMessage,
        action: "danger",
      });
    }
  }, [selectedUserId, selectedRole, addUser, availableUsers, toast]);

  const handleRemoveUser = useCallback(
    async (userKanbanBoardId: string) => {
      try {
        await removeUser(userKanbanBoardId);
        toast.show({
          description: "User removed successfully",
          action: "success",
        });
      } catch (error) {
        toast.show({
          description: formatError(error)?.message || "Failed to remove user",
          action: "danger",
        });
      }
    },
    [removeUser, toast]
  );

  const handleRoleChange = useCallback(
    async (userKanbanBoardId: string, newRole: UserKanbanBoardRole) => {
      try {
        await updateUserRole(userKanbanBoardId, newRole);
        toast.show({
          description: "Role updated successfully",
          action: "success",
        });
      } catch (error) {
        toast.show({
          description: formatError(error)?.message || "Failed to update role",
          action: "danger",
        });
      }
    },
    [updateUserRole, toast]
  );

  /* ---------------------------------------------------------------------
   * SE-8296 — ownership transfer.
   *
   * Gated separately from the membership controls because the server gates it
   * separately: transfer admits the board OWNER row OR an org
   * MANAGER/ADMIN/OWNER, while add/role/remove admit the OWNER row alone.
   * ------------------------------------------------------------------- */

  const { canManageMembers, canTransferOwnership } =
    useBoardPermissions(boardId);

  const { transferOwnership, isTransferring } =
    useBoardOwnershipTransfer(boardId);

  const [selectedNewOwnerId, setSelectedNewOwnerId] = useState<string>("");
  const [isTransferPopoverOpen, setIsTransferPopoverOpen] = useState(false);
  const [transferSearchText, setTransferSearchText] = useState("");
  const [isTransferConfirmOpen, setIsTransferConfirmOpen] = useState(false);
  const [transferErrorMessage, setTransferErrorMessage] = useState<
    string | undefined
  >(undefined);

  const currentOwner = useMemo(
    () => currentUsers.find(user => user.role === UserKanbanBoardRole.Owner),
    [currentUsers]
  );

  const currentOwnerName = useMemo(
    () => (currentOwner ? getUserName(currentOwner) : null),
    [currentOwner]
  );

  /**
   * Whether "no owner on this page" is actually "no owner".
   *
   * The members connection is ordered `role DESC` over a Postgres enum declared
   * (OWNER, EDITOR, VIEWER), so DESC sorts the OWNER LAST — it is the first row
   * cut by any page limit. Raising the page size moves that cliff; it does not
   * remove it. So when no OWNER row was seen AND the connection has another
   * page, ownership is UNRESOLVED, not absent: the board may well have an owner
   * we simply did not fetch.
   *
   * Asserting absence there would tell an org admin "This board has no owner"
   * about a board that has one, and would leave the incumbent in the candidate
   * list, where selecting them yields a server no-op the UI would toast as a
   * successful transfer.
   */
  const isOwnershipUnresolved =
    !currentOwner &&
    (boardData?.getKanbanBoard?.users?.pageInfo?.hasNextPage ?? false);

  /**
   * Candidates come from the ACTIVE organization roster, not the board member
   * list. `listMyOrganizationsOrganizationUsers` defaults to ACTIVE only, which
   * is exactly the predicate the mutation enforces; the board member connection
   * carries no membership status, so sourcing from it would offer a
   * since-deactivated member the server then refuses with NotFoundException.
   * The incumbent is dropped because transferring to them is a silent no-op.
   */
  const transferCandidates = useMemo(() => {
    const allOrgUsers =
      usersData?.listMyOrganizationsOrganizationUsers?.edges?.map(
        edge => edge.node
      ) ?? [];

    const search = transferSearchText.toLowerCase().trim();

    return allOrgUsers
      .filter(
        orgUser => orgUser.user && orgUser.user.id !== currentOwner?.userId
      )
      .filter(
        orgUser =>
          !search ||
          getOrgUserDisplayName(orgUser).toLowerCase().includes(search)
      )
      .map(orgUser => ({
        userId: orgUser.user?.id ?? "",
        displayName: getOrgUserDisplayName(orgUser),
      }));
  }, [usersData, currentOwner, transferSearchText]);

  const selectedNewOwnerDisplayName = useMemo(() => {
    if (!selectedNewOwnerId) return "Select a new owner";
    const match = transferCandidates.find(
      candidate => candidate.userId === selectedNewOwnerId
    );
    return match ? match.displayName : "Select a new owner";
  }, [selectedNewOwnerId, transferCandidates]);

  const transferConfirmBody = useMemo(() => {
    const becomesOwner = `${selectedNewOwnerDisplayName} becomes the owner of this board.`;

    // The demotion is stated because the server performs it and the user
    // cannot see it coming: the incumbent keeps access, as EDITOR, rather
    // than losing the board they built.
    return currentOwnerName
      ? [becomesOwner, `${currentOwnerName} stays on the board as an editor.`]
      : [becomesOwner];
  }, [selectedNewOwnerDisplayName, currentOwnerName]);

  const handleTransferPopoverOpen = useCallback(() => {
    setIsTransferPopoverOpen(true);
  }, []);

  const handleTransferPopoverClose = useCallback(() => {
    setIsTransferPopoverOpen(false);
    setTransferSearchText("");
  }, []);

  const handleTransferPress = useCallback(() => {
    setTransferErrorMessage(undefined);
    setIsTransferConfirmOpen(true);
  }, []);

  const handleTransferCancel = useCallback(() => {
    setIsTransferConfirmOpen(false);
    setTransferErrorMessage(undefined);
  }, []);

  const handleTransferConfirm = useCallback(async () => {
    try {
      await transferOwnership(selectedNewOwnerId);
      setIsTransferConfirmOpen(false);
      setSelectedNewOwnerId("");
      toast.show({
        description: "Board ownership transferred",
        action: "success",
      });
    } catch (error) {
      // Held inside the dialog rather than thrown at a toast: the refusals
      // here are specific and actionable, and the dialog stays open so the
      // choice survives a retry.
      setTransferErrorMessage(
        formatError(error)?.message || "Failed to transfer ownership"
      );
    }
  }, [transferOwnership, selectedNewOwnerId, toast]);

  const handleUserPopoverOpen = useCallback(() => {
    setIsUserPopoverOpen(true);
  }, []);

  const handleUserPopoverClose = useCallback(() => {
    setIsUserPopoverOpen(false);
    setUserSearchText("");
  }, []);

  const handleRolePopoverOpen = useCallback(() => {
    setIsRolePopoverOpen(true);
  }, []);

  const handleRolePopoverClose = useCallback(() => {
    setIsRolePopoverOpen(false);
  }, []);

  return (
    <BoardShareSheetView
      isOpen={isOpen}
      onClose={onClose}
      currentUsers={currentUsersWithDisplayData}
      availableUsers={availableUsersWithDisplayData}
      currentUserId={currentUser?.id ?? ""}
      selectedUserId={selectedUserId}
      selectedRole={selectedRole}
      selectedUserDisplayName={selectedUserDisplayName}
      selectedRoleDisplayText={selectedRoleDisplayText}
      loading={boardLoading || usersLoading}
      isAddingUser={isAddingUser}
      isRemovingUser={isRemovingUser}
      onSelectUser={setSelectedUserId}
      onSelectRole={setSelectedRole}
      onAddUser={handleAddUser}
      onRemoveUser={handleRemoveUser}
      onRoleChange={handleRoleChange}
      isUserPopoverOpen={isUserPopoverOpen}
      onUserPopoverOpen={handleUserPopoverOpen}
      onUserPopoverClose={handleUserPopoverClose}
      isRolePopoverOpen={isRolePopoverOpen}
      onRolePopoverOpen={handleRolePopoverOpen}
      onRolePopoverClose={handleRolePopoverClose}
      userSearchText={userSearchText}
      onUserSearchChange={setUserSearchText}
      canManageMembers={canManageMembers}
      canTransferOwnership={canTransferOwnership}
      currentOwnerName={currentOwnerName}
      isOwnershipUnresolved={isOwnershipUnresolved}
      transferCandidates={transferCandidates}
      selectedNewOwnerId={selectedNewOwnerId}
      selectedNewOwnerDisplayName={selectedNewOwnerDisplayName}
      isTransferPopoverOpen={isTransferPopoverOpen}
      onTransferPopoverOpen={handleTransferPopoverOpen}
      onTransferPopoverClose={handleTransferPopoverClose}
      transferSearchText={transferSearchText}
      onTransferSearchChange={setTransferSearchText}
      onSelectNewOwner={setSelectedNewOwnerId}
      onTransferPress={handleTransferPress}
      isTransferConfirmOpen={isTransferConfirmOpen}
      onTransferConfirm={handleTransferConfirm}
      onTransferCancel={handleTransferCancel}
      transferConfirmBody={transferConfirmBody}
      transferErrorMessage={transferErrorMessage}
      isTransferring={isTransferring}
    />
  );
};

/**
 * Container for the board share sheet.
 *
 * SE-8296 — keys the stateful body on the board AND the open state, so every
 * pending selection is dropped whenever the sheet closes or the user switches
 * pipelines. The key-reset pattern rather than an effect, matching
 * ConfirmDialog: the repo bans synchronous setState inside an
 * effect, and a remount is the reset.
 *
 * This is load-bearing, not tidiness. MainContainer renders this sheet
 * unconditionally with isOpen as a prop and no key of its own, so without this
 * the container never unmounts. A new owner chosen for board A would stay
 * selected and the Transfer button live when the sheet reopened on board B,
 * and nothing on screen would contradict it — candidate names come from the
 * organization roster, which is board-independent, so even the confirmation
 * dialog reads plausibly while naming someone picked for a different board.
 * @param props - Component props
 * @returns The keyed stateful sheet
 */
const BoardShareSheetContainer = (props: BoardShareSheetProps) => (
  <BoardShareSheetStateful
    key={`${props.boardId}:${props.isOpen ? "open" : "closed"}`}
    {...props}
  />
);

BoardShareSheetContainer.displayName = "BoardShareSheetContainer";

export default BoardShareSheetContainer;
